import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Lock, ArrowLeft, Check, Plane,
  ShieldCheck, Zap, DollarSign
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { trackEvent } from "@/components/TrackingScripts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProgressBar({ step }) {
  const { t } = useTranslation();
  const steps = [
    { num: 1, label: t("results.progress_search") },
    { num: 2, label: t("results.progress_info") },
    { num: 3, label: t("results.progress_checkout") },
  ];
  return (
    <div className="flex items-center justify-center gap-0 max-w-md mx-auto" data-testid="progress-bar">
      {steps.map((s, i) => (
        <div key={s.num} className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              s.num < step ? "bg-[#22c55e] text-white" : s.num === step ? "bg-[#f97316] text-white" : "bg-[#e2e8f0] text-[#6b7280]"
            }`}>
              {s.num < step ? <Check className="w-4 h-4" /> : s.num}
            </div>
            <span className={`text-xs mt-1 font-medium ${s.num <= step ? "text-[#1e3a5f]" : "text-[#6b7280]"}`}>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`w-16 sm:w-24 h-0.5 mx-2 mt-[-14px] ${s.num < step ? "bg-[#22c55e]" : "bg-[#e2e8f0]"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function Payment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [flight, setFlight] = useState(null);
  const [returnFlight, setReturnFlight] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Check if user came back from cancelled Stripe session
  const cancelled = searchParams.get("cancelled") === "true";

  useEffect(() => {
    const storedFlight = localStorage.getItem("selectedFlight");
    const storedCheckout = localStorage.getItem("checkoutData");
    if (!storedFlight || !storedCheckout) {
      navigate("/");
      return;
    }
    setFlight(JSON.parse(storedFlight));
    setCheckout(JSON.parse(storedCheckout));
    const storedReturn = localStorage.getItem("selectedReturnFlight");
    if (storedReturn) setReturnFlight(JSON.parse(storedReturn));
    trackEvent("add_payment_info");

    if (cancelled) {
      toast.error("Payment was cancelled. You can try again.");
    }
  }, [navigate, cancelled]);

  const handlePay = async () => {
    setProcessing(true);

    try {
      const res = await axios.post(`${API}/payments/create-checkout-session`, {
        flight_data: flight,
        return_flight_data: returnFlight || null,
        passengers: checkout.passengers || [{ title: checkout.title, first_name: checkout.firstName, last_name: checkout.lastName }],
        passenger_email: checkout.email,
        validity_type: checkout.validity,
        add_ons: {
          round_trip: !!returnFlight,
          activate_later: checkout.activateLater || false,
          notes: checkout.notes || "",
        },
        promo_code: checkout.promoCode || null,
      });

      const { checkout_url } = res.data;
      if (checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = checkout_url;
      } else {
        toast.error("Failed to create payment session");
        setProcessing(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment error. Please try again.");
      setProcessing(false);
    }
  };

  if (!flight || !checkout) return null;

  const totalPrice = checkout.totalPrice || 12;
  const primaryPax = checkout.passengers?.[0] || { title: checkout.title, first_name: checkout.firstName, last_name: checkout.lastName };
  const paxCount = checkout.passengers?.length || 1;

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="payment-page">
      <SEOHead title="Secure Payment | OnwardFlighTickets" description="Complete your payment securely for your onward flight reservation." path="/checkout/payment" />
      <Header />
      <main className="pt-20">
        <div className="bg-white border-b border-[#e2e8f0] py-5">
          <ProgressBar step={3} />
        </div>

        <div className="max-w-2xl mx-auto px-6 md:px-8 py-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            {/* Order Summary Card */}
            <div className="bg-white border border-[#e2e8f0] rounded-2xl p-6 mb-6">
              <h2 className="font-['Outfit'] text-lg font-semibold text-[#1e3a5f] mb-5">Order Summary</h2>

              {/* Outbound */}
              <div className="flex items-center gap-4 mb-4">
                {flight.airline_logo && <img src={flight.airline_logo} alt={flight.airline} className="w-8 h-8 object-contain" />}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-[#1e3a5f] font-['Outfit']">{flight.departure_code}</span>
                    <Plane className="w-4 h-4 text-[#f97316] -rotate-45" />
                    <span className="text-lg font-bold text-[#1e3a5f] font-['Outfit']">{flight.arrival_code}</span>
                  </div>
                  <p className="text-xs text-[#6b7280]">{flight.departure_date} &middot; {flight.airline} {flight.flight_number} &middot; {flight.duration_text}</p>
                </div>
              </div>

              {/* Return */}
              {returnFlight && (
                <div className="flex items-center gap-4 mb-4 pt-3 border-t border-[#f1f5f9]">
                  {returnFlight.airline_logo && <img src={returnFlight.airline_logo} alt={returnFlight.airline} className="w-8 h-8 object-contain" />}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[#6b7280]">Return:</span>
                      <span className="text-lg font-bold text-[#1e3a5f] font-['Outfit']">{returnFlight.departure_code}</span>
                      <Plane className="w-4 h-4 text-[#f97316] -rotate-45" />
                      <span className="text-lg font-bold text-[#1e3a5f] font-['Outfit']">{returnFlight.arrival_code}</span>
                    </div>
                    <p className="text-xs text-[#6b7280]">{returnFlight.departure_date} &middot; {returnFlight.airline} {returnFlight.flight_number}</p>
                  </div>
                </div>
              )}

              {/* Passenger & Details */}
              <div className="border-t border-[#e2e8f0] pt-4 mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#6b7280]">Passenger{paxCount > 1 ? "s" : ""}</span>
                  <span className="text-[#1f2937] font-medium">
                    {primaryPax.title || primaryPax.first_name?.charAt(0)?.toUpperCase()} {primaryPax.first_name} {primaryPax.last_name}
                    {paxCount > 1 && ` +${paxCount - 1} other${paxCount > 2 ? "s" : ""}`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6b7280]">Validity</span>
                  <span className="text-[#1f2937] font-medium">{checkout.validity?.toUpperCase()}</span>
                </div>
                {checkout.discountAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#22c55e] font-medium">Promo ({checkout.promoCode})</span>
                    <span className="text-[#22c55e] font-medium">-${checkout.discountAmount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-[#f1f5f9] pt-3 mt-3">
                  <span className="text-[#1e3a5f] font-semibold">Total</span>
                  <span className="text-2xl font-bold text-[#f97316] font-['Outfit']" data-testid="payment-total">${totalPrice.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Pay Button */}
            <button
              onClick={handlePay}
              disabled={processing}
              className="w-full bg-[#1e3a5f] hover:bg-[#152e4d] text-white rounded-2xl py-4 px-6 font-semibold text-base transition-all flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="pay-button"
            >
              {processing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Redirecting to secure payment...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  Pay ${totalPrice.toFixed(2)}
                </>
              )}
            </button>

            {/* Trust Signals */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="flex flex-col items-center text-center">
                <ShieldCheck className="w-5 h-5 text-[#22c55e] mb-1" />
                <p className="text-xs text-[#6b7280]">256-bit SSL encrypted</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <DollarSign className="w-5 h-5 text-[#22c55e] mb-1" />
                <p className="text-xs text-[#6b7280]">Money-back guarantee</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <Zap className="w-5 h-5 text-[#22c55e] mb-1" />
                <p className="text-xs text-[#6b7280]">Instant delivery</p>
              </div>
            </div>

            <p className="text-center text-xs text-[#94a3b8] mt-4">
              Secure checkout powered by Stripe. Apple Pay, Google Pay, and all major cards accepted.
            </p>

            {/* Back button */}
            <button
              onClick={() => navigate("/checkout")}
              className="flex items-center gap-2 text-sm text-[#6b7280] hover:text-[#1e3a5f] mt-6 mx-auto"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to passenger details
            </button>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
